/**
 * @typedef {Object} CategoryUpdateItem
 * @property {string} [id] - If provided, update existing; if not, create new.
 * @property {string} name - The name of the category.
 * @property {string} [description] - The description of the category.
 * @property {string} [color] - The color of the category.
 * @property {boolean} [isActive] - Whether the category is active.
 */

/**
 * @typedef {Object} BulkUpdateCategoriesDto
 * @property {CategoryUpdateItem[]} categories - An array of category update items.
 */

import configs from "../configs";
import { getAuth0Token } from "../ui/utils";
import { normalizeResolvedThemeResponse } from "../ui/branding/themeContract.js";
import { getTenantHost } from "../utils/tenantHost.js";

const defaultSiteSettingsCache = new Map();

function getDefaultSiteSettingsCacheKey(tenantHost) {
  return tenantHost || "platform";
}

function getDefaultSiteSettingsPath(tenantHost) {
  return tenantHost ? "current" : "platform";
}

export function invalidateDefaultSiteSettingsCache() {
  defaultSiteSettingsCache.clear();
}

export async function fetchDefaultSiteSettings(fetch) {
  const tenantHost = getTenantHost();
  const cacheKey = getDefaultSiteSettingsCacheKey(tenantHost);
  const cached = defaultSiteSettingsCache.get(cacheKey);

  if (cached?.loaded) {
    return cached.value;
  }

  if (!cached?.promise) {
    const promise = (async () => {
      const token = await getAuth0Token();
      const path = getDefaultSiteSettingsPath(tenantHost);
      const url = `${configs.PERSISTENCE_API_URL}/site-settings/${path}`;
      const headers = {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(tenantHost ? { "X-Tenant-Host": tenantHost } : {})
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers
      });

      if (!response.ok) {
        if (response.status === 404) {
          defaultSiteSettingsCache.set(cacheKey, { loaded: true, value: null });
          return null;
        }
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();
      const value = {
        ...json,
        theme: normalizeResolvedThemeResponse(json)
      };
      defaultSiteSettingsCache.set(cacheKey, { loaded: true, value });
      return value;
    })().catch(error => {
      const latest = defaultSiteSettingsCache.get(cacheKey);
      if (latest?.promise === promise) {
        defaultSiteSettingsCache.delete(cacheKey);
      }
      throw error;
    });

    defaultSiteSettingsCache.set(cacheKey, { loaded: false, promise });
  }

  return defaultSiteSettingsCache.get(cacheKey).promise;
}

export default class SiteSettingsService {
  constructor() {
    this.baseUrl = `${configs.PERSISTENCE_API_URL}/site-settings`;
  }

  /**
   * Get the platform site settings
   * @param {Object} fetch - Fetch function
   * @returns {Promise<Object>} Platform site settings (returns 404 if none exists)
   */
  async getDefault(fetch) {
    try {
      return await fetchDefaultSiteSettings(fetch);
    } catch (e) {
      console.error("Error fetching platform site settings:", e);
      throw e;
    }
  }

  /**
   * Create new platform site settings
   * @param {Object} fetch - Fetch function
   * @param {Object} data - Site settings data
   * @returns {Promise<Object>} Created site settings
   */
  async create(fetch, data) {
    const token = await getAuth0Token();
    const url = `${this.baseUrl}/platform`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Network Error: ${response.statusText}`);
      }

      const json = await response.json();
      invalidateDefaultSiteSettingsCache();
      return json;
    } catch (e) {
      console.error("Error creating site settings:", e);
      throw e;
    }
  }

  /**
   * Get current user's site settings
   * @param {Object} fetch - Fetch function
   * @returns {Promise<Object>} Site settings
   */
  async getMine(fetch) {
    const token = await getAuth0Token();
    const url = `${this.baseUrl}/me`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();
      return json;
    } catch (e) {
      console.error("Error fetching user site settings:", e);
      throw e;
    }
  }

  /**
   * Get site settings by ID
   * @param {Object} fetch - Fetch function
   * @param {string} id - Site settings ID
   * @returns {Promise<Object>} Site settings object
   */
  async findById(fetch, id) {
    const token = await getAuth0Token();
    const url = `${this.baseUrl}/${id}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Site settings not found");
        }
        throw new Error(`Network Error: ${response.statusText}`);
      }

      const json = await response.json();
      return json;
    } catch (e) {
      console.error("Error fetching site settings:", e);
      throw e;
    }
  }

  /**
   * Update site settings
   * @param {Object} fetch - Fetch function
   * @param {string} id - Site settings ID
   * @param {Object} data - Updated site settings data (includes enableCategories)
   * @returns {Promise<Object>} Updated site settings
   */
  async update(fetch, id, data) {
    const token = await getAuth0Token();
    const url = `${this.baseUrl}/${id}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `Network Error: ${response.statusText}`);
      }

      const json = await response.json();
      invalidateDefaultSiteSettingsCache();
      return json;
    } catch (e) {
      console.error("Error updating site settings:", e);
      throw e;
    }
  }

}
